/**
 * Coupons Manager Demo
 *
 * This demonstrates the comprehensive coupons management system for WooCommerce.
 */

import React from "react";

const CouponsDemo: React.FC = () => {
  const sampleCoupons = [
    {
      code: "SAVE20",
      type: "Percentage",
      amount: "20%",
      description: "20% off all products",
      expires: "2025-12-31",
      usage: "45/100",
      status: "Active",
    },
    {
      code: "WELCOME10",
      type: "Fixed Cart",
      amount: "$10.00",
      description: "Welcome discount for new customers",
      expires: "Never",
      usage: "123/∞",
      status: "Active",
    },
    {
      code: "FLASH50",
      type: "Fixed Product",
      amount: "$5.00",
      description: "Flash sale product discount",
      expires: "2025-10-15",
      usage: "8/50",
      status: "Active",
    },
    {
      code: "BIRTHDAY25",
      type: "Percentage",
      amount: "25%",
      description: "Birthday special discount",
      expires: "2025-11-01",
      usage: "0/20",
      status: "Draft",
    },
  ];

  const features = [
    {
      title: "Complete CRUD Operations",
      description:
        "Create, read, update, and delete coupons with full form validation",
      icon: "fas fa-edit",
    },
    {
      title: "Multiple Discount Types",
      description: "Percentage, fixed cart, and fixed product discount options",
      icon: "fas fa-percentage",
    },
    {
      title: "Advanced Filtering",
      description: "Search by code, filter by status and discount type",
      icon: "fas fa-filter",
    },
    {
      title: "Usage Tracking",
      description: "Monitor coupon usage with limits and restrictions",
      icon: "fas fa-chart-bar",
    },
    {
      title: "Expiry Management",
      description: "Set expiration dates and usage limits per user",
      icon: "fas fa-calendar-alt",
    },
    {
      title: "Email Restrictions",
      description: "Limit coupon usage to specific email addresses",
      icon: "fas fa-envelope",
    },
  ];

  const discountTypes = [
    {
      type: "Percentage Discount",
      description: "Reduces the total by a percentage (e.g., 20% off)",
      example: "20% off entire cart",
      badge: "success",
    },
    {
      type: "Fixed Cart Discount",
      description: "Reduces the cart total by a fixed amount",
      example: "$10 off orders over $50",
      badge: "primary",
    },
    {
      type: "Fixed Product Discount",
      description: "Reduces individual product prices by a fixed amount",
      example: "$5 off each item",
      badge: "info",
    },
  ];

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <h3 className="mb-4">Coupons Manager</h3>

          <div className="alert alert-info mb-4">
            <h6>New Feature: Complete Coupon Management System</h6>
            <p className="mb-0">
              Full-featured coupons manager with create, edit, delete
              capabilities, advanced filtering, and comprehensive WooCommerce
              API integration.
            </p>
          </div>

          {/* Features Overview */}
          <div className="row mb-5">
            {features.map((feature, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-3">
                <div className="card h-100">
                  <div className="card-body text-center">
                    <div className="mb-3">
                      <i className={`${feature.icon} fa-2x text-primary`}></i>
                    </div>
                    <h6 className="card-title">{feature.title}</h6>
                    <p className="card-text small text-muted">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Discount Types */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">📊 Supported Discount Types</h6>
            </div>
            <div className="card-body">
              <div className="row">
                {discountTypes.map((type, index) => (
                  <div key={index} className="col-md-4 mb-3">
                    <div className="border rounded p-3 h-100">
                      <h6>
                        <span className={`badge bg-${type.badge} me-2`}>
                          {type.type}
                        </span>
                      </h6>
                      <p className="mb-2">{type.description}</p>
                      <div className="small text-muted">
                        <strong>Example:</strong> {type.example}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sample Coupons Table */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">📋 Sample Coupons</h6>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Usage</th>
                    <th>Expires</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleCoupons.map((coupon, index) => (
                    <tr key={index}>
                      <td>
                        <div>
                          <strong className="text-primary">
                            {coupon.code}
                          </strong>
                          <div className="small text-muted">
                            {coupon.description}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            coupon.type === "Percentage"
                              ? "bg-success"
                              : coupon.type === "Fixed Cart"
                              ? "bg-primary"
                              : "bg-info"
                          }`}
                        >
                          {coupon.type}
                        </span>
                      </td>
                      <td>
                        <strong>{coupon.amount}</strong>
                      </td>
                      <td>{coupon.usage}</td>
                      <td>{coupon.expires}</td>
                      <td>
                        <span
                          className={`badge ${
                            coupon.status === "Active"
                              ? "bg-success"
                              : "bg-warning"
                          }`}
                        >
                          {coupon.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Usage Instructions */}
          <div className="row">
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">🎯 Key Features</h6>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <strong>Smart Filtering:</strong> Search by code, status,
                      and type
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <strong>Pagination:</strong> Efficient loading with page
                      navigation
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <strong>Usage Tracking:</strong> Monitor how often coupons
                      are used
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <strong>Restrictions:</strong> Set usage limits, email
                      restrictions
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <strong>Expiry Dates:</strong> Set automatic expiration
                      dates
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <strong>Status Management:</strong> Draft, active, pending
                      states
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">🚀 Getting Started</h6>
                </div>
                <div className="card-body">
                  <ol className="ps-3">
                    <li className="mb-2">
                      <strong>Create a Coupon:</strong> Click "Create Coupon"
                      button
                    </li>
                    <li className="mb-2">
                      <strong>Set Basic Info:</strong> Enter code, type, and
                      amount
                    </li>
                    <li className="mb-2">
                      <strong>Configure Restrictions:</strong> Set limits and
                      restrictions
                    </li>
                    <li className="mb-2">
                      <strong>Set Options:</strong> Individual use, free
                      shipping, etc.
                    </li>
                    <li className="mb-2">
                      <strong>Save & Activate:</strong> Coupon is ready for
                      customers
                    </li>
                    <li className="mb-2">
                      <strong>Monitor Usage:</strong> Track performance and
                      usage
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h6>💡 Pro Tips:</h6>
            <div className="row">
              <div className="col-md-6">
                <ul>
                  <li>
                    <strong>Percentage vs Fixed:</strong> Use percentage for
                    broader appeal, fixed amounts for specific promotions
                  </li>
                  <li>
                    <strong>Usage Limits:</strong> Set limits to control
                    campaign costs
                  </li>
                  <li>
                    <strong>Email Restrictions:</strong> Perfect for targeted
                    customer campaigns
                  </li>
                </ul>
              </div>
              <div className="col-md-6">
                <ul>
                  <li>
                    <strong>Expiry Dates:</strong> Create urgency with
                    limited-time offers
                  </li>
                  <li>
                    <strong>Individual Use:</strong> Prevent coupon stacking
                    when needed
                  </li>
                  <li>
                    <strong>Free Shipping:</strong> Combine discounts with
                    shipping incentives
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponsDemo;
